
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Input, Modal, Select, Tag, message } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import RowActionsMenu from "../components/RowActionsMenu";
import useBusinessMutationInvalidation from "../hooks/useBusinessMutationInvalidation";
import ServiceDetailsModal from "./ServiceDetailsModal";
import CatalogWorkspace, { CatalogModal, CatalogTable } from "../components/catalog/CatalogWorkspace";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import { catalogApi as apiClient, catalogConfig, readCatalogPages, useCatalogResource, useCatalogSearch } from "../components/catalog/catalogResources";
import { catalogId, catalogMoney, formatServiceDuration, requireReference, verifyCatalogDraft } from "../components/catalog/catalogPresentation";
import useServiceFormOptions from "./useServiceFormOptions";
import ServiceFormContent from "./ServiceFormContent";


const PERMISSIONS = {
  VIEW: "Gestion Commerciale.Services.view",
  CREATE: "Gestion Commerciale.Services.create",
  EDIT: "Gestion Commerciale.Services.edit",
  DELETE: "Gestion Commerciale.Services.delete",
};

const toPositiveIntOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const toNonNegativeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return parsed;
};

const normalizeWarehouseForPayload = (warehouseValue) => {
  if (!warehouseValue || warehouseValue === "__global__") return null;
  return toPositiveIntOrNull(warehouseValue);
};

const normalizeComponentsInput = (rawComponents) => {
  const normalized = [];
  let hasIncompleteRows = false;

  (Array.isArray(rawComponents) ? rawComponents : []).forEach((row, index) => {
    const hasAnyValue =
      row &&
      (row.product_id !== undefined ||
        row.quantity_per_service !== undefined ||
        row.wastage_percent !== undefined ||
        row.sort_order !== undefined);

    if (!hasAnyValue) return;

    const productId = toPositiveIntOrNull(row?.product_id);
    const quantityPerService = Number(row?.quantity_per_service);

    if (!productId || !Number.isFinite(quantityPerService) || quantityPerService <= 0) {
      hasIncompleteRows = true;
      return;
    }

    const wastagePercent = toNonNegativeNumber(row?.wastage_percent, 0);
    const sortOrderCandidate = Number(row?.sort_order);
    const sortOrder =
      Number.isInteger(sortOrderCandidate) && sortOrderCandidate >= 0
        ? sortOrderCandidate
        : index + 1;

    normalized.push({
      product_id: productId,
      quantity_per_service: quantityPerService,
      wastage_percent: wastagePercent,
      sort_order: sortOrder,
    });
  });

  return {
    rows: normalized,
    hasIncompleteRows,
  };
};

const isBillingEligibleProduct = (product) => {
  const productType = String(product?.product_type || "").toLowerCase();
  const productionKind = String(product?.production_kind || "finished").toLowerCase();
  if (productType === "raw") return false;
  if (productType === "production" && productionKind !== "finished") return false;
  return true;
};

const GestionServices = () => {
  const invalidateServices = useBusinessMutationInvalidation("service");
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [detailSelection, setDetailSelection] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    status: "active",
    warehouse: "all",
  });
  const [costPreview, setCostPreview] = useState(null);
  const [costPreviewLoading, setCostPreviewLoading] = useState(false);
  const [costPreviewError, setCostPreviewError] = useState(null);
  const [form] = Form.useForm();
  const previewRequestIdRef = useRef(0);

  const { user } = useAuth();
  const { selectedCompany: companySelection, selectedWarehouse: warehouseSelection } = useSelection();
  const selectedCompany = catalogId(companySelection), selectedWarehouse = catalogId(warehouseSelection);
  const [page, setPage] = useState(1);
  const [productSearch, setProductSearch] = useState("");
  const productQuery = useCatalogSearch(productSearch);
  const filterSearch = useCatalogSearch(filters.search);
  const contextRef = useRef("");
  contextRef.current = selectedCompany + ":" + selectedWarehouse + ":" + modalOpen;
  const previewAbortRef = useRef(null);
  useEffect(() => () => { contextRef.current = ""; previewRequestIdRef.current += 1; previewAbortRef.current?.abort(); }, []);

  const hasPermission = useCallback(
    (permissionKey) => user?.permissions?.includes(permissionKey) || false,
    [user]
  );

  const canView = hasPermission(PERMISSIONS.VIEW);
  const canCreate = hasPermission(PERMISSIONS.CREATE);
  const canEdit = hasPermission(PERMISSIONS.EDIT);
  const canDelete = hasPermission(PERMISSIONS.DELETE);
  const detailOpen = Boolean(canView && detailSelection
    && String(detailSelection.companyId) === String(selectedCompany)
    && String(detailSelection.selectedWarehouseId || "") === String(selectedWarehouse || ""));

  useEffect(() => { setDetailSelection(null); }, [selectedCompany, selectedWarehouse]);

  const watchedAllValues = Form.useWatch([], form);
  const watchedWarehouseId = Form.useWatch("warehouse_id", form);
  const watchedBillingProductId = Form.useWatch("product_id", form);
  const watchedComponents = Form.useWatch("components", form) || [];

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      warehouse: selectedWarehouse ? String(selectedWarehouse) : "all",
    }));
  }, [selectedCompany, selectedWarehouse]);

  const scope = { user, companyId: selectedCompany, warehouseId: selectedWarehouse };
  const selectedProductIds = [...new Set([watchedBillingProductId, ...watchedComponents.map((row) => row?.product_id)]
    .map(Number).filter((id) => Number.isInteger(id) && id > 0))].sort((a, b) => a - b);
  const options = useServiceFormOptions(scope, modalOpen && canView, productQuery, selectedProductIds);
  const warehouseResource = useCatalogResource("catalog-warehouse-options", scope,
    (signal) => readCatalogPages("/api/warehouses", "warehouses", selectedCompany, signal), [], false, canView);
  const warehouses = options.data?.warehouses || warehouseResource.data || [];
  const products = options.data?.products || [];
  const warehousesLoading = options.loading || warehouseResource.loading;
  const serviceList = useCatalogResource("service-list", scope, async (signal) => {
    const params = { page, limit: 20, search: filterSearch, status: filters.status };
    if (filters.warehouse !== "all") params.warehouse_id = filters.warehouse === "__global__" ? "null" : filters.warehouse;
    if (selectedWarehouse) params.preview_warehouse_id = selectedWarehouse;
    return (await apiClient.get("/api/services", catalogConfig(selectedCompany, signal, params))).data;
  }, [page, filterSearch, filters.status, filters.warehouse], true, canView);
  const services = serviceList.data?.services || [];
  const loading = serviceList.loading;
  const fetchServices = () => { void serviceList.refresh().catch(() => {}); };
  useEffect(() => { setPage(1); }, [filterSearch, filters.status, filters.warehouse, selectedCompany]);
  useEffect(() => {
    setModalOpen(false); setEditingService(null); setCostPreview(null); setCostPreviewError(null);
    setProductSearch(""); form.resetFields(); previewRequestIdRef.current += 1; previewAbortRef.current?.abort();
  }, [selectedCompany, selectedWarehouse, form]);

  useEffect(() => {
    if (!modalOpen) return;
    if (!watchedWarehouseId || watchedWarehouseId === "__global__") return;
    form.setFieldValue("preview_warehouse_id", String(watchedWarehouseId));
  }, [modalOpen, watchedWarehouseId, form]);

  const warehouseOptions = useMemo(
    () =>
      warehouses.map((warehouse) => ({
        value: String(warehouse.id),
        label: warehouse.name,
      })),
    [warehouses]
  );

  const billingProductOptions = useMemo(
    () =>
      products
        .filter(isBillingEligibleProduct)
        .map((product) => ({
          value: Number(product.id),
          label: product.name,
        })),
    [products]
  );

  const componentProductOptions = useMemo(
    () =>
      products.map((product) => ({
        value: Number(product.id),
        label: product.name,
        title: product.stock_management_mode ? "Mode de stock : " + product.stock_management_mode : undefined,
      })),
    [products]
  );

  const openCreateModal = () => {
    if (!selectedCompany) {
      message.warning("Selectionnez d'abord une entreprise.");
      return;
    }

    setEditingService(null);
    setCostPreview(null);
    setCostPreviewLoading(true);
    setCostPreviewError(null);
    form.resetFields();
    form.setFieldsValue({
      warehouse_id: selectedWarehouse ? String(selectedWarehouse) : "__global__",
      preview_warehouse_id: selectedWarehouse ? String(selectedWarehouse) : undefined,
      product_id: undefined,
      code: "",
      name: "",
      description: "",
      duration_minutes: null,
      base_price: 0,
      labor_cost: 0,
      overhead_cost: 0,
      components: [],
      is_active: true,
    });
    setModalOpen(true);
  };

  const openEditModal = (service) => {
    setEditingService(service);
    setCostPreview(service?.cost_preview || null);
    setCostPreviewLoading(true);
    setCostPreviewError(null);
    form.resetFields();
    form.setFieldsValue({
      warehouse_id: service.warehouse_id ? String(service.warehouse_id) : "__global__",
      preview_warehouse_id: service.warehouse_id
        ? String(service.warehouse_id)
        : selectedWarehouse
        ? String(selectedWarehouse)
        : undefined,
      product_id: service.product_id ? Number(service.product_id) : undefined,
      code: service.code || "",
      name: service.name || "",
      description: service.description || "",
      duration_minutes: service.duration_minutes || null,
      base_price: Number(service.base_price || 0),
      labor_cost: Number(service.labor_cost || 0),
      overhead_cost: Number(service.overhead_cost || 0),
      components: Array.isArray(service.components)
        ? service.components.map((component, index) => ({
            product_id: Number(component.product_id),
            quantity_per_service: Number(component.quantity_per_service || 0),
            wastage_percent: Number(component.wastage_percent || 0),
            sort_order: Number(component.sort_order || index + 1),
          }))
        : [],
      is_active: Number(service.is_active) === 1,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingService(null);
    setCostPreview(null);
    setCostPreviewError(null);
    setCostPreviewLoading(false);
    previewRequestIdRef.current += 1; previewAbortRef.current?.abort();
    form.resetFields();
  };

  const requestCostPreview = useCallback(
    async (values) => {
      if (!selectedCompany || !modalOpen) return;
      const requestId = ++previewRequestIdRef.current;
      setCostPreviewLoading(false);
      previewAbortRef.current?.abort();
      const controller = new AbortController(); previewAbortRef.current = controller;

      const basePriceRaw = Number(values?.base_price);
      if (!Number.isFinite(basePriceRaw) || basePriceRaw < 0) {
        setCostPreview(null);
        setCostPreviewError("Le tarif de base doit etre un nombre positif ou nul.");
        return;
      }

      const { rows: normalizedComponents, hasIncompleteRows } = normalizeComponentsInput(
        values?.components
      );
      if (hasIncompleteRows) {
        setCostPreview(null);
        setCostPreviewError(
          "Completez chaque composant (produit et quantite) avant de continuer."
        );
        return;
      }

      const warehouseId = normalizeWarehouseForPayload(values?.warehouse_id);
      const previewWarehouseField = toPositiveIntOrNull(values?.preview_warehouse_id);
      const fallbackWarehouseId = toPositiveIntOrNull(selectedWarehouse);
      const previewWarehouseId = previewWarehouseField || warehouseId || fallbackWarehouseId;

      if (normalizedComponents.length > 0 && !previewWarehouseId) {
        setCostPreview(null);
        setCostPreviewError(
          "Ajoutez un établissement de simulation pour calculer le cout des composants."
        );
        return;
      }

      setCostPreviewLoading(true);
      setCostPreviewError(null);

      try {
        const payload = {
          company_id: selectedCompany,
          service_id: editingService ? Number(editingService.id) : undefined,
          warehouse_id: warehouseId,
          preview_warehouse_id: previewWarehouseId || undefined,
          product_id: values?.product_id ? Number(values.product_id) : null,
          base_price: toNonNegativeNumber(values?.base_price, 0),
          labor_cost: toNonNegativeNumber(values?.labor_cost, 0),
          overhead_cost: toNonNegativeNumber(values?.overhead_cost, 0),
          components: normalizedComponents,
        };

        const response = await apiClient.post("/api/services/cost-preview", payload, catalogConfig(selectedCompany, controller.signal));
        if (requestId !== previewRequestIdRef.current) return;
        setCostPreview(response.data?.cost_preview || null);
        setCostPreviewError(null);
      } catch (error) {
        if (requestId !== previewRequestIdRef.current) return;
        const backendMessage =
          error.response?.data?.error ||
          "Impossible de calculer le cout du service pour le moment.";
        setCostPreview(null);
        setCostPreviewError(backendMessage);
      } finally {
        if (requestId === previewRequestIdRef.current) {
          setCostPreviewLoading(false);
        }
      }
    },
    [editingService, modalOpen, selectedCompany, selectedWarehouse]
  );

  useEffect(() => {
    if (!modalOpen) return undefined;
    previewRequestIdRef.current += 1; previewAbortRef.current?.abort();
    setCostPreviewLoading(true);
    const timeout = setTimeout(() => {
      const currentValues = watchedAllValues || form.getFieldsValue(true);
      requestCostPreview(currentValues);
    }, 300);
    return () => clearTimeout(timeout);
  }, [modalOpen, watchedAllValues, form, requestCostPreview, options.updatedAt]);

  const handleSubmit = async (values) => {
    if (!selectedCompany) {
      message.error("Aucune entreprise selectionnee.");
      return;
    }

    const { rows: normalizedComponents, hasIncompleteRows } = normalizeComponentsInput(
      values.components
    );
    if (hasIncompleteRows) {
      message.error("Chaque composant doit avoir un produit et une quantite.");
      return;
    }

    if (costPreviewError) {
      message.error(costPreviewError);
      return;
    }

    if (costPreview?.blocking_reasons?.length > 0) {
      message.error(costPreview.blocking_reasons[0]);
      return;
    }

    if (costPreview && Number(costPreview.pricing_gap || 0) < 0) {
      message.error("Le tarif du service est inferieur au cout calcule.");
      return;
    }

    const warehouseId = normalizeWarehouseForPayload(values.warehouse_id);
    const previewWarehouseField = toPositiveIntOrNull(values.preview_warehouse_id);
    const fallbackWarehouseId = toPositiveIntOrNull(selectedWarehouse);
    const previewWarehouseId = previewWarehouseField || warehouseId || fallbackWarehouseId;

    const payload = {
      company_id: selectedCompany,
      warehouse_id: warehouseId,
      preview_warehouse_id: previewWarehouseId || undefined,
      product_id: values.product_id ? Number(values.product_id) : null,
      code: values.code?.trim() || null,
      name: values.name?.trim(),
      description: values.description?.trim() || null,
      duration_minutes: values.duration_minutes || null,
      base_price: toNonNegativeNumber(values.base_price, 0),
      labor_cost: toNonNegativeNumber(values.labor_cost, 0),
      overhead_cost: toNonNegativeNumber(values.overhead_cost, 0),
      components: normalizedComponents,
      is_active: values.is_active ? 1 : 0,
    };

    setSubmitting(true);
    try {
      const draft = JSON.stringify(form.getFieldsValue(true)), capturedContext = contextRef.current;
      const fresh = await verifyCatalogDraft(options, form, contextRef);
      requireReference(fresh.warehouses, values.warehouse_id, "L’établissement");
      requireReference(fresh.warehouses, values.preview_warehouse_id, "L’établissement du calcul");
      [values.product_id, ...normalizedComponents.map((row) => row.product_id)].filter(Boolean)
        .forEach((id) => requireReference(fresh.selectedProducts, id, "Le produit sélectionné"));
      const checked = (await apiClient.post("/api/services/cost-preview", payload, catalogConfig(selectedCompany))).data?.cost_preview;
      if (capturedContext !== contextRef.current || draft !== JSON.stringify(form.getFieldsValue(true))) throw new Error("La saisie ou le contexte a changé. Vérifiez le formulaire.");
      if (!checked || checked.blocking_reasons?.length || !checked.pricing_ok || Number(checked.pricing_gap) < 0) {
        throw new Error(checked?.blocking_reasons?.[0] || "Le tarif ne couvre pas le coût actualisé du service.");
      }
      if (editingService) {
        await apiClient.put(`/api/services/${editingService.id}`, payload);
        message.success("Service mis a jour avec succes.");
      } else {
        await apiClient.post("/api/services", payload);
        message.success("Service cree avec succes.");
      }
      void invalidateServices();
      closeModal();
      fetchServices();
    } catch (error) {
      const backendMessage = error.response?.data?.error;
      message.error(backendMessage || error.message || "Erreur lors de la sauvegarde du service.");
      console.error("[SERVICES] Error saving service:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (service) => {
    Modal.confirm({
      title: "Supprimer ce service ?",
      content:
        "Cette action est definitive. Verifiez qu'il n'est plus utilise avant suppression.",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      centered: true,
      onOk: async () => {
        try {
          await apiClient.delete(`/api/services/${service.id}`);
          void invalidateServices();
          message.success("Service supprime avec succes.");
          fetchServices();
        } catch (error) {
          const backendMessage = error.response?.data?.error;
          message.error(backendMessage || "Erreur lors de la suppression du service.");
          console.error("[SERVICES] Error deleting service:", error);
        }
      },
    });
  };

  const openDetail = (record) => setDetailSelection({ id: record.id, companyId: selectedCompany,
    warehouseId: record.warehouse_id || selectedWarehouse || null, selectedWarehouseId: selectedWarehouse });
  const columns = [
    { title: "Service", dataIndex: "name", key: "name" },
    { title: "Tarif", dataIndex: "base_price", key: "price", width: 145, align: "right", render: catalogMoney },
    { title: "Durée", dataIndex: "duration_minutes", key: "duration", width: 110, render: formatServiceDuration },
    { title: "Établissement", dataIndex: "warehouse_name", key: "warehouse", width: "23%", render: (value) => value || "Global entreprise" },
    { title: "Statut", dataIndex: "is_active", key: "status", width: 105, render: (value) => <Tag color={Number(value) === 1 ? "success" : "default"}>{Number(value) === 1 ? "Actif" : "Inactif"}</Tag> },
    { title: "Actions", key: "actions", width: 76, align: "center", render: (_, record) => <RowActionsMenu
      ariaLabel={"Afficher les actions du service " + (record.name || record.id)}
      items={[{ key: "detail", icon: <EyeOutlined />, label: "Voir le détail", onClick: () => openDetail(record) },
        canEdit && { key: "edit", icon: <EditOutlined />, label: "Modifier", onClick: () => openEditModal(record) },
        canDelete && { key: "delete", icon: <DeleteOutlined />, danger: true, label: "Supprimer", onClick: () => handleDelete(record) }].filter(Boolean)} /> },
  ];

  const hasBlockingReasons = (costPreview?.blocking_reasons || []).length > 0;
  const hasNegativePricingGap = Number(costPreview?.pricing_gap || 0) < 0;
  const disableSave =
    submitting || !costPreview || options.loading || Boolean(options.error) ||
    costPreviewLoading ||
    Boolean(costPreviewError) ||
    hasBlockingReasons ||
    hasNegativePricingGap;

  if (!canView) return <Alert type="warning" showIcon message="Vous n’avez pas la permission d’afficher les services." />;
  if (!selectedCompany) return <Alert type="info" showIcon message="Sélectionnez une entreprise pour gérer les services." />;
  return <CatalogWorkspace title="Services" subtitle="Définissez vos prestations, leurs consommables et leur tarif."
    action={canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>Nouveau service</Button>}>
    <div className="catalog-filters"><label className="catalog-filter catalog-filter--search"><span>Rechercher</span>
      <Input allowClear prefix={<SearchOutlined />} placeholder="Rechercher un service ou un code" value={filters.search}
        onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))} /></label>
      <label className="catalog-filter"><span>Statut</span><Select value={filters.status} onChange={(status) => setFilters((prev) => ({ ...prev, status }))}
        options={[{ value: "active", label: "Actifs" }, { value: "inactive", label: "Inactifs" }, { value: "all", label: "Tous" }]} /></label>
      <label className="catalog-filter"><span>Établissement</span><Select value={filters.warehouse} loading={warehousesLoading}
        onChange={(warehouse) => setFilters((prev) => ({ ...prev, warehouse }))}
        options={[{ value: "all", label: "Tous les établissements" }, { value: "__global__", label: "Global entreprise" }, ...warehouseOptions]} /></label>
    </div>
    <BusinessResourceNotice resource={serviceList} label="les services" />
    <div className="catalog-count">{serviceList.data?.pagination?.totalItems ?? services.length} services</div>
    <CatalogTable columns={columns} dataSource={services} loading={loading} onDetail={openDetail}
      pagination={{ current: page, pageSize: 20, total: serviceList.data?.pagination?.totalItems ?? services.length, onChange: setPage }} />
    <p className="catalog-list-note">Cliquez sur une ligne pour consulter le détail.</p>
    <ServiceDetailsModal open={detailOpen} serviceId={detailSelection?.id} userId={user?.id}
      companyId={selectedCompany} warehouseId={detailSelection?.warehouseId} previewWarehouseId={selectedWarehouse}
      previewWarehouseName={warehouses.find((row) => String(row.id) === String(selectedWarehouse))?.name}
      canEdit={canEdit} onClose={() => setDetailSelection(null)}
      onEdit={(service) => { if (canEdit && detailOpen) { setDetailSelection(null); openEditModal(service); } }} />
    <CatalogModal title={editingService ? "Modifier le service" : "Nouveau service"} open={modalOpen} onCancel={() => { if (!submitting) closeModal(); }}
      subtitle="Définissez la prestation, son tarif et ses consommables."
      footer={<><Button disabled={submitting} onClick={closeModal}>Annuler</Button><Button type="primary" disabled={disableSave} loading={submitting}
        onClick={() => form.submit()}>{editingService ? "Enregistrer les modifications" : "Créer le service"}</Button></>}>
      <BusinessResourceNotice resource={options} label="les choix du service" />
      <Form form={form} size="large" layout="vertical" onFinish={handleSubmit} initialValues={{ is_active: true }}>
        <ServiceFormContent form={form} values={watchedAllValues || {}} warehouses={warehouseOptions}
          products={componentProductOptions} billingProducts={billingProductOptions} preview={costPreview}
          previewLoading={costPreviewLoading} previewError={costPreviewError} onProductSearch={setProductSearch} />
      </Form>
    </CatalogModal>
  </CatalogWorkspace>;
};
export default GestionServices;
